<template>
  <div
    id="viewer"
    class="pdf-reader"
    data-test="text-content"
  />
</template>

<script>
import WebViewer from '@pdftron/pdfjs-express-viewer';
import { getInstance } from '@pdftron/pdfjs-express-viewer';
import { RESOURCE_TYPE_PDF } from "@/assets/constants/constants.js";
import { getWithAuthentication } from "@/services/api";
import { BASE_URL, WORD_EXTRACTION_ENDPOINT } from "@/assets/constants/api_endpoints.js";

/**
 * PDFViewer component for displaying non-scanned PDFs
 * Handles text selection events and basic PDF functionality
 */
export default {
    name: 'PDFViewer',
    props: {
        pdfFile: {
            type: Blob,
            required: true
        },
        activeSentenceText: {
            type: [String, Array],
            default: null
        }
    },
    emits: ['documentLoaded', 'textSelected', 'pageChanged'],
    data() {
        return {
            docViewer: null,
            pdfInstance: null,
            currentPage: 0,
            selectedText: null,
            touchStartX: 0,
            touchStartY: 0,
            pageCharQuadCache: {},
        };
    },

    watch: {
        pdfFile: {
            handler() {
                this.$nextTick(() => {
                    this.initializeViewer();
                });
            },
            immediate: false
        },
        activeSentenceText(newText) {
            this.highlightSentence(newText);
        }
    },

    mounted() {
        if (this.pdfFile) {
            this.initializeViewer();
        }
        window.addEventListener('demo:simulate-text-selection', this._onDemoTextSelection);
        this._onIframeTouchStart = this._onIframeTouchStart.bind(this);
        this._onIframeTouchEnd = this._onIframeTouchEnd.bind(this);
    },

    beforeUnmount() {
        const iframe = this._getViewerIframe();
        const iframeDoc = iframe?.contentDocument || iframe?.contentWindow?.document;
        if (iframeDoc) {
            iframeDoc.removeEventListener('touchstart', this._onIframeTouchStart, true);
            iframeDoc.removeEventListener('touchend', this._onIframeTouchEnd, true);
            if (iframeDoc.body) {
                iframeDoc.body.removeEventListener('touchstart', this._onIframeTouchStart, true);
                iframeDoc.body.removeEventListener('touchend', this._onIframeTouchEnd, true);
            }
        }
        this.dispose();
        window.removeEventListener('demo:simulate-text-selection', this._onDemoTextSelection);
    },
    methods: {
        async initializeViewer() {
            const element = document.getElementById('viewer');
            const instance = getInstance();
            
            if (instance) {
                instance.dispose();
            }

            try {
                await WebViewer({
                    licenseKey: process.env.VUE_APP_PDF_VIEWER_KEY,
                    path: `${process.env.BASE_URL}public`
                }, element).then((instance) => {
                    instance.UI.loadDocument(this.pdfFile, { filename: 'viewing_file.pdf' });
                    this.docViewer = instance.Core.documentViewer;
                    this.pdfInstance = instance;

                    this.setupTextSelectionListener();
                    this.setupPageChangeListener();

                    this.docViewer.addEventListener('documentLoaded', () => {
                        setTimeout(() => {
                            instance.UI.setLayoutMode(instance.UI.LayoutMode.Continuous);
                            instance.UI.setFitMode(instance.UI.FitMode.FitWidth);
                        }, 0);
                        this._attachIframeTouchListeners();
                        setTimeout(() => this._attachIframeTouchListeners(), 300);
                        setTimeout(() => this._attachIframeTouchListeners(), 1000);
                        this.$emit('documentLoaded');
                    });
                });
            } catch (error) {
                throw error;
            }
        },

        setupTextSelectionListener() {
            if (!this.docViewer) return;

            try {
                this.docViewer.addEventListener('textSelected', (quads, text, pageNumber) => {
                    this.selectedText = text;
                });
                this.docViewer.addEventListener('mouseLeftUp', () => {
                    if (this.selectedText) {
                        this.$emit('textSelected', {
                            text: this.selectedText,
                            pageNumber: null,
                            quads: null,
                        });
                    }
                });
                console.log("Text selection listener registered");
            } catch (err) {
                console.log("Error setting up textSelected listener:", err.message);
            }
        },

        setupPageChangeListener() {
            if (!this.docViewer) return;

            this.docViewer.addEventListener('pageNumberUpdated', (pageNumber) => {
                this.currentPage = pageNumber;
                this.$emit('pageChanged', pageNumber);
            });
        },

        getCurrentPage() {
            return this.docViewer?.getCurrentPage() || 0;
        },

        highlightSentence(text) {
            if (!this.docViewer || !this.pdfInstance) return;

            // Ensure UI search is not broken by any prior textSearchInit call
            if (typeof this.docViewer.cancelTextSearch === 'function') {
                this.docViewer.cancelTextSearch();
            }

            this._clearHighlightCanvases();
            if (!text) return;
            const sentences = Array.isArray(text) ? text : [text];
            this._doHighlight(sentences);
        },

        async _doHighlight(sentences) {
            const doc = this.docViewer.getDocument();
            if (!doc) return;

            const pageCount = this.docViewer.getPageCount();
            // Accumulate all quads per page so multiple sentences on the same page
            // are all drawn in a single canvas pass (avoids canvas recreate wiping prior highlights)
            const quadsByPage = {};
            const pageInfoByPage = {};

            for (let pageNum = 1; pageNum <= pageCount; pageNum++) {
                const pageText = await doc.loadPageText(pageNum);
                if (!pageText) continue;

                for (const sentence of sentences) {
                    if (!sentence) continue;
                    const idx = pageText.indexOf(sentence);
                    if (idx === -1) continue;

                    try {
                        const quads = await doc.getTextPosition(pageNum, idx, idx + sentence.length);
                        if (!quads || !quads.length) continue;
                        if (!quadsByPage[pageNum]) {
                            quadsByPage[pageNum] = [];
                            pageInfoByPage[pageNum] = doc.getPageInfo(pageNum);
                        }
                        quadsByPage[pageNum].push(...quads);
                    } catch (e) {
                        console.error(`[PDFViewer] getTextPosition failed on page ${pageNum}:`, e);
                    }
                }
            }

            for (const pageNum of Object.keys(quadsByPage)) {
                this._drawHighlightOnPage(Number(pageNum), quadsByPage[pageNum], pageInfoByPage[pageNum]);
            }
        },

        _getViewerIframe() {
            const container = document.getElementById('viewer');
            if (container) {
                const iframe = container.querySelector('iframe');
                if (iframe) return iframe;
            }
            return document.querySelector('iframe[id^="webviewer"]');
        },

        _clearHighlightCanvases() {
            const iframe = this._getViewerIframe();
            if (!iframe) return;
            const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
            if (!iframeDoc) return;
            iframeDoc.querySelectorAll('canvas[id^="voice-hl-"]').forEach(c => c.remove());
        },

        _drawHighlightOnPage(pageNum, quads, pageInfo) {
            const iframe = this._getViewerIframe();
            if (!iframe) return;
            const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
            if (!iframeDoc) return;

            // Try multiple selectors used across PDFTron / PDF.js Express versions
            const pageEl =
                iframeDoc.getElementById(`pageSection${pageNum}`) ||
                iframeDoc.querySelector(`[data-page-number="${pageNum}"]`) ||
                iframeDoc.querySelector(`.page[data-page-number="${pageNum}"]`);

            if (!pageEl) {
                console.warn(`[PDFViewer] page element not found for page ${pageNum}`);
                return;
            }

            const existing = iframeDoc.getElementById(`voice-hl-${pageNum}`);
            if (existing) existing.remove();

            const canvas = iframeDoc.createElement('canvas');
            canvas.id = `voice-hl-${pageNum}`;
            canvas.style.cssText = 'position:absolute;top:0;left:0;pointer-events:none;z-index:9999;';

            let w = pageEl.offsetWidth;
            let h = pageEl.offsetHeight;
            if (w === 0 || h === 0) {
                const computed = (iframeDoc.defaultView || iframe.contentWindow).getComputedStyle(pageEl);
                w = parseInt(computed.width) || pageEl.scrollWidth || 595;
                h = parseInt(computed.height) || pageEl.scrollHeight || 842;
            }
            canvas.width = w;
            canvas.height = h;

            if (!pageEl.style.position || pageEl.style.position === 'static') {
                pageEl.style.position = 'relative';
            }
            if (pageEl.style.overflow === 'hidden') {
                pageEl.style.overflow = 'visible';
            }
            pageEl.appendChild(canvas);

            const ctx = canvas.getContext('2d');
            ctx.fillStyle = 'rgba(255, 230, 0, 0.4)';

            // getTextPosition returns coordinates in screen space (top-left origin, Y down)
            // so scale directly without Y-flip
            const scaleX = pageInfo ? w / pageInfo.width : 1;
            const scaleY = pageInfo ? h / pageInfo.height : 1;

            quads.forEach(quad => {
                ctx.beginPath();
                ctx.moveTo(quad.x1 * scaleX, quad.y1 * scaleY);
                ctx.lineTo(quad.x2 * scaleX, quad.y2 * scaleY);
                ctx.lineTo(quad.x3 * scaleX, quad.y3 * scaleY);
                ctx.lineTo(quad.x4 * scaleX, quad.y4 * scaleY);
                ctx.closePath();
                ctx.fill();
            });
        },

        dispose() {
            this._clearHighlightCanvases();
            const instance = getInstance();
            if (instance) {
                instance.dispose();
            }
        },

        _onDemoTextSelection(event) {
            const text = event.detail?.text;
            if (text) {
                this.$emit('textSelected', { text, pageNumber: null, quads: null });
            }
        },

        _attachIframeTouchListeners() {
            const iframe = this._getViewerIframe();
            if (!iframe) return;
            const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
            if (!iframeDoc) return;

            iframeDoc.removeEventListener('touchstart', this._onIframeTouchStart, true);
            iframeDoc.removeEventListener('touchend', this._onIframeTouchEnd, true);
            iframeDoc.addEventListener('touchstart', this._onIframeTouchStart, { passive: true, capture: true });
            iframeDoc.addEventListener('touchend', this._onIframeTouchEnd, { passive: true, capture: true });

            if (iframeDoc.body) {
                iframeDoc.body.removeEventListener('touchstart', this._onIframeTouchStart, true);
                iframeDoc.body.removeEventListener('touchend', this._onIframeTouchEnd, true);
                iframeDoc.body.addEventListener('touchstart', this._onIframeTouchStart, { passive: true, capture: true });
                iframeDoc.body.addEventListener('touchend', this._onIframeTouchEnd, { passive: true, capture: true });
            }
        },

        _onIframeTouchStart(event) {
            const touch = event.touches[0];
            if (touch) {
                this.touchStartX = touch.clientX;
                this.touchStartY = touch.clientY;
            }
            this.selectedText = null;
            const iframe = this._getViewerIframe();
            iframe?.contentWindow?.getSelection()?.removeAllRanges();
        },

        async _onIframeTouchEnd(event) {
            const touch = event.changedTouches[0];
            const isTap = touch &&
                Math.abs(touch.clientX - this.touchStartX) <= 8 &&
                Math.abs(touch.clientY - this.touchStartY) <= 8;

            // Wait for PDFTron to finalize any selection
            setTimeout(async () => {
                // Long-press selection: PDFTron already set this.selectedText via the textSelected event
                if (this.selectedText?.trim()) {
                    this.$emit('textSelected', { text: this.selectedText.trim(), pageNumber: null, quads: null });
                    this.selectedText = null;
                    return;
                }

                if (!isTap || !touch) return;

                // Single tap: programmatically select the word at the tap point using PDFTron
                try {
                    const iframe = this._getViewerIframe();
                    const iframeDoc = iframe?.contentDocument || iframe?.contentWindow?.document;
                    if (!iframeDoc || !this.pdfInstance) return;

                    // Try PDFTron's selectWordsInRect with a small rect around the tap
                    const textTool = this._getTextSelectTool();
                    if (textTool?.selectWordsInRect) {
                        const tapPage = this._getTappedPageNumber(iframeDoc, touch) ?? this.docViewer.getCurrentPage();
                        if (!tapPage) return;

                        const radii = [4, 8, 12];
                        for (const radius of radii) {
                            this.selectedText = null;
                            this._activateTextSelectTool();
                            const rect = new this.pdfInstance.Core.Math.Rect(
                                touch.clientX - radius,
                                touch.clientY - radius,
                                touch.clientX + radius,
                                touch.clientY + radius
                            );
                            textTool.selectWordsInRect(rect, tapPage);
                            await new Promise(resolve => setTimeout(resolve, 120));
                            if (!this.selectedText?.trim()) continue;

                            const selected = this.selectedText.trim();
                            const char = selected[0] ?? '';
                            const url = `${WORD_EXTRACTION_ENDPOINT}?text=${encodeURIComponent(selected)}&char=${encodeURIComponent(char)}`;
                            const response = await getWithAuthentication(BASE_URL, url);

                            if (response?.status === 200) {
                                const data = JSON.parse(response.data);
                                const extractedWord = data?.word || selected;
                                this.$emit('textSelected', { text: extractedWord, pageNumber: null, quads: null });
                            } else {
                                this.$emit('textSelected', { text: selected, pageNumber: null, quads: null });
                            }

                            this.selectedText = null;
                            return;
                        }
                    } else {
                        const tapPage = this._getTappedPageNumber(iframeDoc, touch) ?? this.docViewer.getCurrentPage();
                        if (!tapPage) return;

                        const extractedFromTap = await this._extractWordFromTapByCharacterMap(tapPage, touch, iframeDoc);
                        if (!extractedFromTap?.word) return;

                        const url = `${WORD_EXTRACTION_ENDPOINT}?text=${encodeURIComponent(extractedFromTap.word)}&char=${encodeURIComponent(extractedFromTap.char || '')}`;
                        const response = await getWithAuthentication(BASE_URL, url);
                        if (response?.status === 200) {
                            const data = JSON.parse(response.data);
                            const normalized = data?.word || extractedFromTap.word;
                            const normalizedQuads = await this._getWordQuadsNearIndex(
                                tapPage,
                                normalized,
                                extractedFromTap.centerIdx
                            );
                            if (Array.isArray(normalizedQuads) && normalizedQuads.length > 0) {
                                this._showTransientTapHighlight(tapPage, normalizedQuads, 900);
                            }
                            this.$emit('textSelected', { text: normalized, pageNumber: null, quads: null });
                        } else {
                            if (Array.isArray(extractedFromTap.quads) && extractedFromTap.quads.length > 0) {
                                this._showTransientTapHighlight(tapPage, extractedFromTap.quads, 900);
                            }
                            this.$emit('textSelected', { text: extractedFromTap.word, pageNumber: null, quads: null });
                        }
                    }
                } catch (e) {
                    console.warn('[PDFViewer] touch word selection failed:', e);
                }
            }, 150);
        },

        async _extractWordFromTapByCharacterMap(pageNum, touch, iframeDoc) {
            const doc = this.docViewer?.getDocument?.();
            if (!doc) return null;

            const pageEl =
                iframeDoc.getElementById(`pageSection${pageNum}`) ||
                iframeDoc.querySelector(`[data-page-number="${pageNum}"]`) ||
                iframeDoc.querySelector(`.page[data-page-number="${pageNum}"]`);
            if (!pageEl) return null;

            const pageRect = pageEl.getBoundingClientRect();
            const pageInfo = doc.getPageInfo(pageNum);
            if (!pageInfo || pageRect.width <= 0 || pageRect.height <= 0) return null;

            // Convert viewport tap coordinates to PDF page coordinates.
            const pageX = ((touch.clientX - pageRect.left) / pageRect.width) * pageInfo.width;
            const pageY = ((touch.clientY - pageRect.top) / pageRect.height) * pageInfo.height;

            if (!this.pageCharQuadCache[pageNum]) {
                const text = await doc.loadPageText(pageNum);
                const chars = [];
                for (let i = 0; i < text.length; i++) {
                    const ch = text[i];
                    if (!ch || /\s/.test(ch)) continue;

                    try {
                        const quads = await doc.getTextPosition(pageNum, i, i + 1);
                        if (!quads || !quads.length) continue;

                        // Use first quad for this character range.
                        const q = quads[0];
                        const minX = Math.min(q.x1, q.x2, q.x3, q.x4);
                        const maxX = Math.max(q.x1, q.x2, q.x3, q.x4);
                        const minY = Math.min(q.y1, q.y2, q.y3, q.y4);
                        const maxY = Math.max(q.y1, q.y2, q.y3, q.y4);
                        chars.push({ index: i, minX, maxX, minY, maxY });
                    } catch {
                        // Skip chars that cannot be mapped.
                    }
                }

                this.pageCharQuadCache[pageNum] = {
                    text,
                    chars,
                };
            }

            const cache = this.pageCharQuadCache[pageNum];
            if (!cache?.chars?.length) return null;

            // Prefer containing box; fallback to nearest character center.
            let best = null;
            let bestDistance = Number.POSITIVE_INFINITY;
            for (const entry of cache.chars) {
                const contains =
                    pageX >= entry.minX &&
                    pageX <= entry.maxX &&
                    pageY >= entry.minY &&
                    pageY <= entry.maxY;

                if (contains) {
                    best = entry;
                    break;
                }

                const cx = (entry.minX + entry.maxX) / 2;
                const cy = (entry.minY + entry.maxY) / 2;
                const dx = pageX - cx;
                const dy = pageY - cy;
                const dist = (dx * dx) + (dy * dy);
                if (dist < bestDistance) {
                    bestDistance = dist;
                    best = entry;
                }
            }

            if (!best) return null;

            const fullText = cache.text;
            const centerIdx = best.index;
            const left = fullText.slice(Math.max(0, centerIdx - 24), centerIdx + 1);
            const right = fullText.slice(centerIdx, Math.min(fullText.length, centerIdx + 24));

            // Build word around tapped char (latin + kana + kanji + prolonged sound mark).
            const leftMatch = left.match(/[A-Za-z0-9_\u3040-\u30FF\u3400-\u9FFFー]+$/);
            const rightMatch = right.match(/^[A-Za-z0-9_\u3040-\u30FF\u3400-\u9FFFー]+/);
            const leftPart = leftMatch ? leftMatch[0] : '';
            const rightPart = rightMatch ? rightMatch[0] : '';
            const word = `${leftPart}${rightPart.slice(1)}`.trim();
            const char = fullText[centerIdx] || '';
            const startIndex = centerIdx - leftPart.length + 1;
            const endIndex = startIndex + word.length;

            if (!word) return null;
            let quads = [];
            try {
                quads = await doc.getTextPosition(pageNum, startIndex, endIndex);
            } catch {
                quads = [];
            }
            return { word, char, startIndex, endIndex, centerIdx, quads };
        },

        async _getWordQuadsNearIndex(pageNum, word, centerIdx) {
            if (!word) return [];
            const doc = this.docViewer?.getDocument?.();
            const pageCache = this.pageCharQuadCache?.[pageNum];
            if (!doc || !pageCache?.text) return [];

            const text = pageCache.text;
            let start = -1;
            let searchFrom = 0;
            let bestStart = -1;
            let bestDistance = Number.POSITIVE_INFINITY;

            while (true) {
                start = text.indexOf(word, searchFrom);
                if (start === -1) break;
                const end = start + word.length;
                const distance = Math.abs(centerIdx - start);
                const containsCenter = centerIdx >= start && centerIdx < end;

                if (containsCenter) {
                    bestStart = start;
                    break;
                }

                if (distance < bestDistance) {
                    bestDistance = distance;
                    bestStart = start;
                }
                searchFrom = start + 1;
            }

            if (bestStart === -1) return [];

            try {
                return await doc.getTextPosition(pageNum, bestStart, bestStart + word.length);
            } catch {
                return [];
            }
        },

        _showTransientTapHighlight(pageNum, quads, durationMs = 900) {
            const iframe = this._getViewerIframe();
            if (!iframe) return;
            const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
            if (!iframeDoc || !Array.isArray(quads) || quads.length === 0) return;

            const pageEl =
                iframeDoc.getElementById(`pageSection${pageNum}`) ||
                iframeDoc.querySelector(`[data-page-number="${pageNum}"]`) ||
                iframeDoc.querySelector(`.page[data-page-number="${pageNum}"]`);

            if (!pageEl) return;

            const existing = iframeDoc.getElementById(`tap-hl-${pageNum}`);
            if (existing) existing.remove();

            const canvas = iframeDoc.createElement('canvas');
            canvas.id = `tap-hl-${pageNum}`;
            canvas.style.cssText = 'position:absolute;top:0;left:0;pointer-events:none;z-index:10001;';

            let w = pageEl.offsetWidth;
            let h = pageEl.offsetHeight;
            if (w === 0 || h === 0) {
                const computed = (iframeDoc.defaultView || iframe.contentWindow).getComputedStyle(pageEl);
                w = parseInt(computed.width) || pageEl.scrollWidth || 595;
                h = parseInt(computed.height) || pageEl.scrollHeight || 842;
            }
            canvas.width = w;
            canvas.height = h;

            if (!pageEl.style.position || pageEl.style.position === 'static') {
                pageEl.style.position = 'relative';
            }
            pageEl.appendChild(canvas);

            const pageInfo = this.docViewer?.getDocument?.().getPageInfo(pageNum);
            const scaleX = pageInfo ? w / pageInfo.width : 1;
            const scaleY = pageInfo ? h / pageInfo.height : 1;
            const ctx = canvas.getContext('2d');

            ctx.fillStyle = 'rgba(255, 196, 0, 0.38)';
            quads.forEach((quad) => {
                ctx.beginPath();
                ctx.moveTo(quad.x1 * scaleX, quad.y1 * scaleY);
                ctx.lineTo(quad.x2 * scaleX, quad.y2 * scaleY);
                ctx.lineTo(quad.x3 * scaleX, quad.y3 * scaleY);
                ctx.lineTo(quad.x4 * scaleX, quad.y4 * scaleY);
                ctx.closePath();
                ctx.fill();
            });

            setTimeout(() => {
                const stale = iframeDoc.getElementById(`tap-hl-${pageNum}`);
                if (stale) stale.remove();
            }, durationMs);
        },

        _getTextSelectTool() {
            if (!this.pdfInstance?.Core?.documentViewer) return null;

            const viewer = this.pdfInstance.Core.documentViewer;
            const toolModeMap = viewer.getToolModeMap?.() || {};

            // Support multiple API shapes across PDF.js Express / WebViewer versions
            return (
                viewer.getTool?.('TextSelect') ||
                viewer.getTool?.('AnnotationCreateTextHighlight') ||
                toolModeMap.TextSelect ||
                toolModeMap.AnnotationCreateTextHighlight ||
                null
            );
        },

        _activateTextSelectTool() {
            if (!this.pdfInstance?.UI) return;

            const ui = this.pdfInstance.UI;
            const toolNames = ['TextSelect', 'AnnotationCreateTextHighlight'];
            for (const toolName of toolNames) {
                try {
                    if (typeof ui.setToolMode === 'function') {
                        ui.setToolMode(toolName);
                    }
                } catch {
                }
            }
        },

        _getTappedPageNumber(iframeDoc, touch) {
            const target = iframeDoc.elementFromPoint(touch.clientX, touch.clientY);
            if (!target || typeof target.closest !== 'function') return null;

            const pageEl =
                target.closest('[data-page-number]') ||
                target.closest('.page[data-page-number]') ||
                target.closest('[id^="pageSection"]');

            if (!pageEl) return null;
            const dataPage = pageEl.getAttribute('data-page-number');
            if (dataPage) return Number(dataPage);

            if (pageEl.id?.startsWith('pageSection')) {
                const parsed = Number(pageEl.id.replace('pageSection', ''));
                return Number.isNaN(parsed) ? null : parsed;
            }

            return null;
        },

    }
};
</script>

<style scoped>
.pdf-reader {
    width: 100%;
    height: 100%;
    position: relative;
}
</style>
